/**
 * How many characters of a project's label a picker row may occupy.
 *
 * Measured against the built stylesheet rather than guessed, at the Copilot
 * panel's 360px width: the control comes out 332px wide, of which 14px is the
 * left padding and 40px is the room the chevron needs, leaving 278px.
 *
 * A count of characters is a bound on length, not on width, and the field
 * renders in a proportional font — so this is sized for the names projects
 * actually have rather than for the worst string that could be typed. Measured
 * at this budget: ordinary mixed-case text comes out around 236px and fits
 * with room to spare; thirty-three capital Ws come out at 417px and do not.
 * The pathological case is still roughly half of what it was before any of
 * this, which is as far as a plain `<option>` can be pushed — its text cannot
 * be styled, so measuring each row and cutting to pixels would mean giving up
 * the native control, and that was chosen for a measured reason of its own.
 *
 * The connect dialog is wider and could afford a few more characters, but uses
 * the same number. One budget for both is worth more than those characters: it
 * is the narrow panel that decides whether a name can be read at all, and two
 * budgets would mean one project reading two different ways.
 */
export const PROJECT_LABEL_MAX_CHARS = 34

const SEPARATOR = ' — '
const ELLIPSIS = '…'

/**
 * The whole of what a project is called, for a tooltip.
 *
 * @param projectKey Backlog's short code, e.g. `BRAI`.
 * @param name The project's full name; a blank one leaves just the key.
 */
export function projectFullLabel(projectKey: string, name: string): string {
  const trimmed = name.trim()
  return trimmed ? `${projectKey}${SEPARATOR}${trimmed}` : projectKey
}

/**
 * What one project reads as in a picker, short enough to stay inside it
 * (BRAI-703).
 *
 * A native `<select>` cannot ellipsise its own text. The closed control clips
 * mid-glyph with nothing to say that anything was cut, and the open list is
 * drawn by the browser at whatever width its longest option asks for — which
 * on a 360px panel is most of the screen. Neither is reachable from CSS, so
 * the string itself is the only lever there is.
 *
 * The key is never cut. It is what people quote to each other and what
 * identifies the project: a name shortened to nothing still leaves a row the
 * reader can act on, while half a key names nothing at all. A key longer than
 * the whole budget is therefore returned over budget rather than mangled.
 *
 * @param projectKey Backlog's short code, e.g. `BRAI`.
 * @param name The project's full name.
 * @param max Characters the row may occupy, the ellipsis included.
 * @returns The label to draw; {@link projectFullLabel} is what the tooltip says.
 */
export function projectOptionLabel(
  projectKey: string,
  name: string,
  max: number = PROJECT_LABEL_MAX_CHARS
): string {
  const full = projectFullLabel(projectKey, name)
  if (full.length <= max) return full

  const room = max - projectKey.length - SEPARATOR.length - ELLIPSIS.length
  if (room < 1) return projectKey
  // Trailing space trimmed after the cut, so a label never reads "Foo …".
  return `${projectKey}${SEPARATOR}${name.trim().slice(0, room).trimEnd()}${ELLIPSIS}`
}
